import { Breadcrumb, Button, Grid, Message, Modal } from "semantic-ui-react";
import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import { observable } from "mobx";
import ErrorWrapper from "~/components/wrapper/ErrorWrapper";
import Link from "~/components/element/Link";
import RouteWrapper from "~/components/wrapper/RouteWrapper";
import ContentLoader from "~/components/element/ContentLoader";
import SubscriptionEditForm from "~/modules/BillingCentral/components/SubscriptionEditForm";
import ToastMessageStore from "~/stores/ToastMessageStore";
import FormStore from "~/stores/FormStore";
import SubscriptionPageStore from "~/stores/Subscription/SubscriptionPageStore";
import LoadingWrapper from "~/components/wrapper/LoadingWrapper";
import ToastMessageContainer from "~/components/ToastMessageContainer";
import SubscriptionTable from "~/modules/BillingCentral/components/SubscriptionTable";
import CreditCardFormStore from "~/stores/Credit/CreditCardFormStore";
import ProviderStore from "~/stores/Provider/ProviderStore";
import CustomerStore from "~/stores/CustomerStore";

@observer
class SubscriptionCreatePage extends Component {
  @observable subscriptionPageStore = null;

  @observable creditCardFormStore = null;

  constructor(props) {
    super(props);

    const { t } = props;

    document.title = t("billingCentral:createYourSubscription");

    this.subscriptionPageStore = new SubscriptionPageStore();
    this.creditCardFormStore = new CreditCardFormStore();

    this.state = {
      confirmationModalVisible: false,
    };
  }

  componentDidMount() {
    const { showError, redirectTo, actualUserStore } = this.props;
    const { subscriptionStore, otherProductStore, taxStore } = this.subscriptionPageStore;
    const { creditCardStore } = this.creditCardFormStore;

    subscriptionStore
      .loadIndex()
      .then(() => {
        const { activeSubscriptions } = subscriptionStore;

        if (activeSubscriptions.length > 0) {
          redirectTo("/billing-central/subscriptions");
        }
      })
      .catch(error => showError(error.message));

    const { actualUser } = actualUserStore;

    this.bindForm(actualUser);

    creditCardStore.pagination.itemsPerPage = 100;
    creditCardStore.updateSearchParams({
      currency: actualUser.currency,
    });

    creditCardStore.loadIndex().catch(error => showError(error.message));

    taxStore.searchParams.state = actualUser.address.state;
    taxStore.searchParams.country = actualUser.address.country;

    taxStore.loadIndex().catch(error => showError(error.message));

    otherProductStore.loadIndex().catch(error => showError(error.message));
  }

  showConfirmationModal = () => this.setState({ confirmationModalVisible: true });

  hideConfirmationModal = () => this.setState({ confirmationModalVisible: false });

  create() {
    const { t, toastMessageStore, showError, redirectTo } = this.props;
    const { subscriptionStore, formStore, creditCardId } = this.subscriptionPageStore;
    const { model } = formStore;
    const { subscriptionItems } = model;

    subscriptionStore
      .create({ creditCardId, subscriptionItems })
      .then(() => {
        toastMessageStore.addToastMessage(t("billingCentral:subscriptionHasBeenCreated"), "positive", t("success"));
        redirectTo("/billing-central/subscriptions");
      })
      .catch(error => showError(error.message));
  }

  bindForm(actualUser) {
    const initialModel = {
      creditCardId: actualUser.defaultCreditCardId !== null ? actualUser.defaultCreditCardId : "",
      subscriptionItems: [],
    };

    this.subscriptionPageStore.formStore = new FormStore(initialModel);
  }

  render() {
    const { t, toastMessageStore, stripeApiPublishableKey, actualUserStore } = this.props;
    const { confirmationModalVisible } = this.state;
    const {
      otherProductStore,
      subscriptionStore,
      formStore,
      taxStore,
      areFormSubscriptionItemsDirty,
    } = this.subscriptionPageStore;
    const { isSaving: subscriptionSaving } = subscriptionStore;
    const { isLoading: otherProductLoading, otherProducts } = otherProductStore;
    const { taxes: applicableTaxes, isLoading: taxLoading } = taxStore;
    const { model } = formStore;
    const { subscriptionItems } = model;
    const { creditCardStore } = this.creditCardFormStore;
    const { isLoading: creditCardLoading } = creditCardStore;
    const { isLoading: actualUserLoading, actualUser } = actualUserStore;

    if (
      typeof subscriptionItems === "undefined" ||
      actualUserLoading ||
      creditCardLoading ||
      taxLoading ||
      otherProductLoading
    ) {
      return <ContentLoader />;
    }

    return (
      <>
        <LoadingWrapper
          loading={subscriptionSaving}
          message={`${t("billingCentral:creatingYourSubscription")} ${t("waitAMomentPlease")}`}
        />

        <div className="side-page-outer-container">
          <Breadcrumb>
            <Breadcrumb.Section>
              <Link to="/billing-central">{t("billingCentral:billingCentral")}</Link>
            </Breadcrumb.Section>
            <Breadcrumb.Divider />
            <Breadcrumb.Section>
              <Link to="/billing-central/subscriptions">{t("billingCentral:subscriptions")}</Link>
            </Breadcrumb.Section>
            <Breadcrumb.Divider />
            <Breadcrumb.Section active>{t("billingCentral:newSubscription")}</Breadcrumb.Section>
          </Breadcrumb>

          <div className="side-page-header no-flex" style={{ marginBottom: "1rem" }}>
            <div className="side-page-header-grid-wrap">
              <div className="side-page-h1-wrap">
                <h1>{t("billingCentral:createYourSubscription")}</h1>
              </div>
            </div>
          </div>

          <Grid centered className="main-grid">
            <Grid.Column>
              <SubscriptionEditForm
                subscriptionStore={subscriptionStore}
                otherProductStore={otherProductStore}
                formStore={formStore}
                creditCardFormStore={this.creditCardFormStore}
                toastMessageStore={toastMessageStore}
                actualUserStore={actualUserStore}
                onSubmitCallback={() => this.showConfirmationModal()}
                quantityOptions={this.subscriptionPageStore.getQuantityOptions()}
                subtotal={this.subscriptionPageStore.calculateSubtotal(subscriptionItems, otherProducts)}
                totalAmountWithTaxes={this.subscriptionPageStore.getTotalAmountWithTaxes(
                  subscriptionItems,
                  otherProducts,
                  applicableTaxes,
                )}
                areFormSubscriptionItemsDirty={areFormSubscriptionItemsDirty}
                applicableTaxes={applicableTaxes}
                stripeApiPublishableKey={stripeApiPublishableKey}
              />
            </Grid.Column>
          </Grid>
        </div>
        <Modal open={confirmationModalVisible} onClose={() => this.hideConfirmationModal()}>
          <Modal.Header>{t("billingCentral:areYouSureYouWantToCreateTheSubscription")}</Modal.Header>
          <Modal.Content>
            {!actualUser.isUsingExternalInvoicingForSubscriptions &&
              actualUser.defaultCreditCardId !== formStore.model.creditCardId && (
                <Message>
                  <Message.Header>{t("billingCentral:creditCardNotTheDefaultOne")}</Message.Header>
                  <p>{t("billingCentral:creditCardNotTheDefaultOneDescription")}</p>
                </Message>
              )}
            <p>
              {t("billingCentral:createSubscriptionDescriptionLine1")}
              <br />
              {t("billingCentral:createSubscriptionDescriptionLine2")}
            </p>
            <SubscriptionTable
              subscriptionItems={subscriptionItems}
              otherProducts={otherProducts}
              applicableTaxes={applicableTaxes}
              subtotal={this.subscriptionPageStore.calculateSubtotal(subscriptionItems, otherProducts)}
              totalAmountWithTaxes={this.subscriptionPageStore.getTotalAmountWithTaxes(
                subscriptionItems,
                otherProducts,
                applicableTaxes,
              )}
              calculatePriceForDaysCallback={(price, days) =>
                this.subscriptionPageStore.calculatePriceForDays(price, days)
              }
              daysTillNextBillingCycle={0}
            />
          </Modal.Content>
          <Modal.Actions>
            <Button secondary onClick={() => this.hideConfirmationModal()}>
              Cancel
            </Button>
            <Button
              primary
              onClick={() => {
                this.create();
                this.hideConfirmationModal();
              }}
            >
              Ok
            </Button>
          </Modal.Actions>
        </Modal>
        <ToastMessageContainer toastMessageStore={toastMessageStore} />
      </>
    );
  }
}

SubscriptionCreatePage.propTypes = {
  showError: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  toastMessageStore: PropTypes.instanceOf(ToastMessageStore),
  actualUserStore: PropTypes.oneOfType([PropTypes.instanceOf(ProviderStore), PropTypes.instanceOf(CustomerStore)]),
  stripeApiPublishableKey: PropTypes.string.isRequired,
};

export default withTranslation()(ErrorWrapper(RouteWrapper(SubscriptionCreatePage)));
